/**
 * Admin-scope `eventStyleResolver` for `<PooCalendar>` (Backlog E.4).
 *
 * Returns the styling metadata <PooCalendar> needs to render one event
 * pill in the admin bookings view:
 *
 *   { statusClass, accentColor, extraClass? }
 *
 * Provider color is a stable djb2 hash of `provider_id` into a fixed
 * 10-color palette anchored on brand tokens (Orange / Purple / Slate)
 * with 7 supporting hues. The same provider always renders in the
 * same color across renders, sessions, and browsers.
 *
 * Cancelled bookings (Q8(b)+(c)): the resolver branches on
 * `status === "cancelled"` and returns a muted grey accent
 * (`#94A3B8`, Tailwind slate-500 — ≥4.5:1 contrast vs `#fff`) plus
 * the `rbc-evt-cancelled` class which the calendar's CSS layer
 * applies strikethrough + 70% opacity to. Result: completed bookings
 * keep their full-saturation provider color; cancelled ones read as
 * "this one is dead" without disappearing.
 *
 * Why no `chipLabel`: D10 — the page-level legend already labels every
 * visible provider; doubling the provider name on every pill clutters
 * the grid. Pills stay "time · customer".
 */

import { STATUS_CLASS } from "@/components/calendar/useEventStyleResolver";

/**
 * 10-color palette. Slots 0-2 are brand anchors; 3-9 are supporting
 * hues each ≥4.5:1 contrast against the `#fff` event-pill text the
 * calendar applies inline via `style.color = "#fff"` (see
 * PooCalendar.jsx eventPropGetter).
 */
export const ADMIN_PALETTE = [
  "#F89623", // 0 · brand orange
  "#574B8C", // 1 · brand purple
  "#374751", // 2 · brand slate
  "#0E7C7B", // 3 · teal
  "#B23A48", // 4 · brick red
  "#5E60CE", // 5 · indigo
  "#7F8C2A", // 6 · olive
  "#C2185B", // 7 · magenta
  "#3D5A80", // 8 · steel blue
  "#6D4C41", // 9 · cocoa
];

/** Cancelled accent — slate-500 muted grey; contrast ≥ 4.5:1 vs white text. */
export const CANCELLED_ACCENT = "#94A3B8";

/**
 * djb2 string hash. 5-line standard implementation; unsigned 32-bit.
 * Picked over md5/CRC32 because (a) no deps, (b) excellent distribution
 * on short UUID-like strings, (c) deterministic across browsers.
 */
export function djb2(s) {
  let h = 5381;
  const str = String(s);
  for (let i = 0; i < str.length; i++) {
    h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * Stable provider → palette slot. Empty/nullish ids fall to slot 0
 * so the resolver never crashes on a malformed event.
 */
export function providerColor(providerId) {
  if (!providerId) return ADMIN_PALETTE[0];
  return ADMIN_PALETTE[djb2(providerId) % ADMIN_PALETTE.length];
}

/**
 * Build the admin-scope resolver. Stateless — does NOT close over
 * `providers`; the legend uses that list separately. The resolver
 * branches on cancelled status and otherwise hashes provider_id.
 *
 * Caller should `useMemo(() => makeAdminResolver(), [])` so PooCalendar's
 * events-memo doesn't churn on every render (per
 * useEventStyleResolver.js:13-16 inline-arrow warning).
 */
export function makeAdminResolver() {
  return function adminResolver(booking) {
    if (!booking) return { statusClass: "" };
    if (booking.status === "cancelled") {
      return {
        statusClass: STATUS_CLASS[booking.status] || "",
        accentColor: CANCELLED_ACCENT,
        extraClass: "rbc-evt-cancelled",
      };
    }
    return {
      statusClass: STATUS_CLASS[booking.status] || "",
      accentColor: providerColor(booking.provider_id || ""),
    };
  };
}
